import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { gamesApi } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './LimboScreen.css';

const BET_CHIPS = [10, 50, 100, 500];
const DEFAULT_TARGETS = [1.2, 1.6, 2.5, 3.5, 4];

function LimboScreen({ user, onBack, onWalletUpdate }) {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState('50');
  const [target, setTarget] = useState(2);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [animating, setAnimating] = useState(false);

  const targets = state?.allowedTargets ?? DEFAULT_TARGETS;

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  const load = useCallback(() => {
    gamesApi.limboState().then((data) => {
      setState(data);
      setTarget((current) => {
        if (data?.allowedTargets?.length && !data.allowedTargets.includes(current)) {
          return data.allowedTargets.includes(2) ? 2 : data.allowedTargets[0];
        }
        return current;
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const potentialWin = useMemo(
    () => Number((Number(amount || 0) * target).toFixed(2)),
    [amount, target],
  );

  const play = async () => {
    const stake = Number(amount);
    if (!Number.isFinite(stake) || stake <= 0 || stake > walletBalance) {
      setToast('Invalid amount or insufficient balance');
      return;
    }
    setLoading(true);
    setAnimating(true);
    setLastResult(null);
    try {
      const result = await gamesApi.limboPlay({ amount: stake, targetMultiplier: target });
      setTimeout(() => {
        setLastResult(result.bet);
        setAnimating(false);
        setToast(
          result.bet.won
            ? `Won! ${result.bet.resultMultiplier}x (target ${result.bet.targetMultiplier}x) — ₹${result.bet.payout}`
            : `Lost — ${result.bet.resultMultiplier}x (needed ${result.bet.targetMultiplier}x)`,
        );
      }, 800);
      await refreshUser();
      load();
    } catch (err) {
      setAnimating(false);
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const displayMult = animating
    ? '...'
    : lastResult
      ? lastResult.resultMultiplier
      : '1.00';

  return (
    <Box className="limbo-root">
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />

      <Box className="limbo-header">
        <IconButton size="small" onClick={onBack} className="limbo-back">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="limbo-title">Limbo</Typography>
        <Chip size="small" label={`₹${walletBalance.toLocaleString('en-IN')}`} className="limbo-wallet" />
      </Box>

      <Box className="limbo-scroll">
        <Box
          className={`limbo-display ${lastResult?.won ? 'limbo-display--win' : lastResult ? 'limbo-display--loss' : ''} ${animating ? 'limbo-display--pulse' : ''}`}
        >
          <TrendingUpRoundedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
          <Typography className="limbo-mult">{displayMult}x</Typography>
          <Typography className="limbo-display__sub">
            Target {target}x · Win ₹{potentialWin}
          </Typography>
        </Box>

        {state?.recentCrash?.length > 0 && (
          <Box className="limbo-crash-row">
            {state.recentCrash.slice(0, 10).map((row, i) => (
              <span
                key={i}
                className={`limbo-crash-chip ${row.won ? 'won' : 'lost'}`}
              >
                {row.multiplier}x
              </span>
            ))}
          </Box>
        )}

        <Box className="limbo-controls">
          <Typography className="limbo-label">Target multiplier</Typography>
          <Box className="limbo-presets">
            {targets.map((t) => (
              <button
                key={t}
                type="button"
                className={`limbo-preset ${target === t ? 'active' : ''}`}
                onClick={() => setTarget(t)}
              >
                {t}x
              </button>
            ))}
          </Box>

          <Typography className="limbo-label">Bet (₹)</Typography>
          <TextField
            fullWidth
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="limbo-input"
          />
          <Box className="limbo-chips">
            {BET_CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                className={`limbo-chip ${Number(amount) === c ? 'active' : ''}`}
                onClick={() => setAmount(String(c))}
              >
                ₹{c}
              </button>
            ))}
          </Box>

          <Button fullWidth className="limbo-play-btn" onClick={play} disabled={loading || !state?.enabled}>
            Play · {target}x target
          </Button>
        </Box>

        <Box className="limbo-terms">
          <Typography className="limbo-terms__title">Terms &amp; Conditions</Typography>
          <Typography className="limbo-terms__line">
            1. Choose your target multiplier (example: {target}x) and place your bet.
          </Typography>
          <Typography className="limbo-terms__line">
            2. If the result multiplier is higher than your selected target, you win.
          </Typography>
          <Typography className="limbo-terms__line">
            3. Winning payout = Bet Amount x Selected Target Multiplier.
          </Typography>
          <Typography className="limbo-terms__line">
            4. If the result multiplier is lower than the target, the round is a loss.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default LimboScreen;
